import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {Select, type SelectMultiProps} from '../select';
import {
  InfiniteScrollSelect,
  type InfiniteScrollSelectProps,
} from '../infinite-scroll-select';
import {
  defaultFilterOption,
  flattenGroupedOptions,
  isOptionGroup,
  type ControlProps,
  type SelectComponents,
  type SelectGroupedOption,
  type SelectValue,
} from '../_select-internals';
import {SelectCheckboxOption} from './SelectCheckboxOption';
import {createCountTrigger} from './CountTrigger';
import {SelectAllRow} from './SelectAllRow';
import type {SelectOption} from '../../lib/selector-option';
import type {ReactNode} from 'react';

type SelectCheckboxesTriggerRenderer = (
  isOpen: boolean,
  toggle: () => void,
) => React.ReactNode;

type SelectCheckboxesPaginationProps = {
  canLoadMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreItems?: () => void;
  loadingMoreText?: string;
  onSearchChange?: (input: string) => void;
  itemHeight?: number;
  listHeight?: number;
  overscan?: number;
  loadMoreThreshold?: number;
};

type SelectCheckboxesProps<
  T = unknown,
  V extends SelectValue = string,
  L extends ReactNode = string,
> = Omit<
  SelectMultiProps<T, V, L>,
  'isMulti' | 'components' | 'searchPosition' | 'menuHeader' | 'options'
> & {
  options?: SelectGroupedOption<T, V, L>[];
  trigger?: SelectCheckboxesTriggerRenderer;
  components?: SelectComponents<T, V, L>;
  valueText?: string | ((selectedCount: number, total: number) => string);
  allowSelectAll?: boolean;
  selectAllLabel?: string;
  searchable?: boolean;
} & SelectCheckboxesPaginationProps;

function hasPaginationProps(props: SelectCheckboxesPaginationProps) {
  return (
    props.canLoadMore !== undefined ||
    props.isLoadingMore !== undefined ||
    props.loadMoreItems !== undefined ||
    props.onSearchChange !== undefined
  );
}

function makeTriggerSlot<T, V extends SelectValue, L extends ReactNode>(
  render: SelectCheckboxesTriggerRenderer,
) {
  function CustomTrigger(props: ControlProps<T, V, L>) {
    return <>{render(props.isOpen, props.onContainerClick)}</>;
  }
  return CustomTrigger;
}

const passthroughFilter = () => true;

function SelectCheckboxesInternal<
  T = unknown,
  V extends SelectValue = string,
  L extends ReactNode = string,
>(props: SelectCheckboxesProps<T, V, L>, ref: React.Ref<HTMLDivElement>) {
  const {
    options: rawOptions = [],
    value,
    defaultValue,
    onChange,
    trigger,
    components: userComponents,
    valueText,
    allowSelectAll = false,
    selectAllLabel,
    searchable = true,
    filterOption = defaultFilterOption,
    closeMenuOnSelect = false,
    onSearchChange,
    ...paginationAndRest
  } = props;
  const {t} = useTranslation();
  const isPaginated = hasPaginationProps(
    paginationAndRest as SelectCheckboxesPaginationProps,
  );

  const [inputValue, setInputValue] = React.useState('');
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState<SelectOption<T, V, L>[]>(
    () => defaultValue ?? [],
  );
  const currentValue = isControlled ? value : internalValue;
  const selected = React.useMemo(() => currentValue ?? [], [currentValue]);

  const handleChange = React.useCallback<NonNullable<typeof onChange>>(
    (next, meta) => {
      if (!isControlled) setInternalValue(next);
      onChange?.(next, meta);
    },
    [isControlled, onChange],
  );

  const flatRawOptions = React.useMemo(
    () => flattenGroupedOptions(rawOptions),
    [rawOptions],
  );

  const filteredGrouped = React.useMemo<SelectGroupedOption<T, V, L>[]>(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return rawOptions;
    return rawOptions
      .map(item => {
        if (isOptionGroup(item)) {
          const opts = item.options.filter(o => filterOption(o, trimmed));
          return opts.length > 0 ? {...item, options: opts} : null;
        }
        return filterOption(item, trimmed) ? item : null;
      })
      .filter((item): item is SelectGroupedOption<T, V, L> => item !== null);
  }, [rawOptions, inputValue, filterOption]);

  const filteredFlat = React.useMemo(
    () => flattenGroupedOptions(filteredGrouped),
    [filteredGrouped],
  );

  const visibleSelectedCount = filteredFlat.reduce((acc, option) => {
    return acc + (selected.some(s => s.value === option.value) ? 1 : 0);
  }, 0);
  const allVisibleSelected =
    filteredFlat.length > 0 && visibleSelectedCount === filteredFlat.length;

  const handleToggleAll = React.useCallback(() => {
    if (allVisibleSelected) {
      const visibleValues = new Set(filteredFlat.map(option => option.value));
      handleChange(
        selected.filter(s => !visibleValues.has(s.value)),
        {action: 'deselect'},
      );
      return;
    }
    const merged = [...selected];
    for (const option of filteredFlat) {
      if (!merged.some(s => s.value === option.value)) merged.push(option);
    }
    handleChange(merged, {action: 'select'});
  }, [allVisibleSelected, filteredFlat, handleChange, selected]);

  const valueTextRef = React.useRef(valueText);
  const totalCountRef = React.useRef(flatRawOptions.length);
  valueTextRef.current = valueText;
  totalCountRef.current = flatRawOptions.length;

  const Control = React.useMemo(() => {
    if (trigger) return makeTriggerSlot<T, V, L>(trigger);
    return createCountTrigger({
      getValueText: () => valueTextRef.current,
      getTotalCount: () => totalCountRef.current,
    });
  }, [trigger]);

  const components = React.useMemo(
    () =>
      ({
        ...userComponents,
        Control,
        Option: userComponents?.Option ?? SelectCheckboxOption,
      }) as SelectComponents<T, V, L>,
    [userComponents, Control],
  );

  const menuHeader = allowSelectAll ? (
    <SelectAllRow
      label={selectAllLabel ?? t('select_all', {defaultValue: 'Select All'})}
      checked={allVisibleSelected}
      onToggle={handleToggleAll}
    />
  ) : undefined;

  const handleInputChange = React.useCallback(
    (next: string) => {
      setInputValue(next);
      onSearchChange?.(next);
    },
    [onSearchChange],
  );

  const baseSharedProps = {
    ...paginationAndRest,
    value: currentValue,
    onChange: handleChange,
    filterOption: passthroughFilter,
    components,
    closeMenuOnSelect,
    searchPosition: (searchable ? 'dropdown' : 'trigger') as 'dropdown' | 'trigger',
    searchable,
    menuHeader,
    onInputChange: handleInputChange,
    isMulti: true as const,
  };

  if (isPaginated) {
    return (
      <InfiniteScrollSelect
        ref={ref}
        {...({
          ...baseSharedProps,
          options: filteredFlat,
        } as unknown as InfiniteScrollSelectProps<T, V, L> & {isMulti: true})}
      />
    );
  }

  return (
    <Select
      ref={ref}
      {...({
        ...baseSharedProps,
        options: filteredGrouped,
      } as unknown as SelectMultiProps<T, V, L> & {isMulti: true})}
    />
  );
}

type SelectCheckboxesComponent = <
  T = unknown,
  V extends SelectValue = string,
  L extends ReactNode = string,
>(
  props: SelectCheckboxesProps<T, V, L> & {ref?: React.Ref<HTMLDivElement>},
) => React.ReactElement;

const SelectCheckboxes = React.forwardRef(
  SelectCheckboxesInternal,
) as unknown as SelectCheckboxesComponent;

export {SelectCheckboxes};
export type {SelectCheckboxesProps, SelectCheckboxesTriggerRenderer};
